import type { FormControlProps, StyleProps } from '@chakra-ui/react'
import { FormControl, FormErrorMessage, FormHelperText } from '@chakra-ui/react'
import type { AriaAttributes, ReactNode } from 'react'
import React from 'react'
import {
  useController,
  type FieldPath,
  type FieldValues,
  type UseControllerProps
} from 'react-hook-form'
import type { FormLabelProps } from '../../../components/FormLabel'
import { FormLabel } from '../../../components/FormLabel'

// Props that will be injected into wrapped components
export interface FormFieldProps<TValue = unknown, TElement = unknown> {
  value: TValue
  onChange: (value: TValue) => void
  onBlur: () => void
  isDisabled: boolean
  name: string
  ref?: React.Ref<TElement>
}

// Props for the HOC-wrapped component
export type WithFormControlProps<
  TComponentProps,
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = Omit<TComponentProps, keyof FormFieldProps> &
  Omit<UseControllerProps<TFieldValues, TName>, 'disabled'> &
  StyleProps & AriaAttributes & {
    label?: ReactNode
    labelProps?: FormLabelProps
    isDisabled?: boolean
    isRequired?: boolean
    helperText?: ReactNode
    helperTextPosition?: 'top' | 'bottom'
    onChange?: (value: unknown) => void
    onBlur?: (value: unknown) => void
    valueTransform?: (value: unknown) => unknown
    formControlVariant?: FormControlProps['variant']
  }

// Type for the wrapped component that accepts any form field values
export type WrappedFormComponent<TComponentProps> = (<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>(
  props: WithFormControlProps<TComponentProps, TFieldValues, TName> & React.RefAttributes<unknown>
) => React.ReactElement | null) & { displayName?: string }

export interface WithFormControlOptions {
  renderLabel?: boolean
}

type ComponentWithFormFieldProps<TComponentProps, TValue, TElement> =
  | React.ComponentType<TComponentProps & FormFieldProps<TValue, TElement>>
  | React.ForwardRefExoticComponent<Omit<TComponentProps & FormFieldProps<TValue, TElement>, 'ref'> & React.RefAttributes<TElement>>

export function withFormControl<
  TComponentProps extends object,
  TValue = unknown,
  TElement = unknown
>(
  Component: ComponentWithFormFieldProps<TComponentProps, TValue, TElement>,
  options?: WithFormControlOptions
): WrappedFormComponent<TComponentProps> {
  const WrappedComponent = <
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
  >(
    props: WithFormControlProps<TComponentProps, TFieldValues, TName> & { forwardedRef?: React.Ref<TElement> }
  ) => {
    const {
      forwardedRef,
      // Form control props
      name,
      control,
      rules,
      defaultValue,
      shouldUnregister,
      // Enhancement props
      label,
      labelProps,
      isDisabled,
      isRequired,
      helperText,
      helperTextPosition = 'bottom',
      onChange: onChangeProp,
      onBlur: onBlurProp,
      valueTransform,
      formControlVariant,
      // Style props
      ...componentAndStyleProps
    } = props

    // Use react-hook-form controller
    const {
      field,
      fieldState: { error },
      formState: { isSubmitting }
    } = useController({
      name,
      control,
      rules,
      defaultValue,
      shouldUnregister
    })

    // Determine if field should be disabled
    const fieldDisabled = !!isDisabled || isSubmitting

    // Extract style props for FormControl
    const styleProps: StyleProps = {}
    const componentProps: Partial<TComponentProps> = {}

    // List of style prop keys
    const stylePropKeys = ['w', 'h', 'minW', 'minH', 'maxW', 'maxH', 'm', 'mt', 'mr', 'mb', 'ml', 'mx', 'my', 'p', 'pt', 'pr', 'pb', 'pl', 'px', 'py']

    // Separate style props from component props
    Object.keys(componentAndStyleProps).forEach(key => {
      if (stylePropKeys.includes(key)) {
        (styleProps as Record<string, unknown>)[key] = (componentAndStyleProps as Record<string, unknown>)[key]
      } else {
        (componentProps as Record<string, unknown>)[key] = (componentAndStyleProps as Record<string, unknown>)[key]
      }
    })

    // Create enhanced field props
    const enhancedFieldProps: FormFieldProps<TValue, TElement> = {
      value: (valueTransform ? valueTransform(field.value) : field.value) as TValue,
      onChange: (value: TValue) => {
        const transformedValue = valueTransform ? valueTransform(value) : value
        field.onChange(transformedValue)
        onChangeProp?.(transformedValue)
      },
      onBlur: () => {
        field.onBlur()
        const transformedValue = valueTransform ? valueTransform(field.value) : field.value
        onBlurProp?.(transformedValue)
      },
      isDisabled: fieldDisabled,
      name,
      ref: (forwardedRef || field.ref) as React.Ref<TElement>
    }

    // Pass label props to component if renderLabel is false
    const componentPropsWithLabel = options?.renderLabel === false && label
      ? { ...componentProps, label, labelProps }
      : componentProps

    return (
      <FormControl
        isInvalid={!!error}
        isRequired={isRequired}
        variant={formControlVariant}
        {...styleProps}
      >
        {label && options?.renderLabel !== false && (
          <FormLabel {...labelProps}>
            {labelProps?.children ?? label}
          </FormLabel>
        )}

        {helperText && helperTextPosition === 'top' && (
          <FormHelperText>{helperText}</FormHelperText>
        )}

        <Component {...componentPropsWithLabel as TComponentProps} {...enhancedFieldProps} />

        {!error ? (
          helperText && helperTextPosition === 'bottom' && (
            <FormHelperText>{helperText}</FormHelperText>
          )
        ) : (
          <FormErrorMessage>{error.message}</FormErrorMessage>
        )}
      </FormControl>
    )
  }

  // Set display name for debugging
  WrappedComponent.displayName = `withFormControl(${Component.displayName || Component.name || 'Component'})`

  // Create forwardRef wrapper and set its display name
  const ForwardedComponent = React.forwardRef((props: any, ref: any) => (
    <WrappedComponent {...props} forwardedRef={ref} />
  )) as any

  ForwardedComponent.displayName = WrappedComponent.displayName

  const result = ForwardedComponent as WrappedFormComponent<TComponentProps>
  result.displayName = ForwardedComponent.displayName

  return result
}